import { useEffect, useState } from "react";
import { useMatches } from "@tanstack/react-router";
import { useAuthStore } from "@/shared/state/authstore";
import { useCurrentUserProfile } from "@/entities/employee/model/queries";
import { LoadingPage } from "./LoadingPage";
import { ErrorPage } from "./ErrorPage";
import { OrgShell } from "./OrgShell";
import { UserShell } from "./UserShell";
import { OnboardingExperience } from "@/features/onboarding/OnboardingExperience";
import { AccountlessHome } from "@/features/onboarding/AccountlessHome";
import { useRevokedAccounts } from "@/entities/session/hooks/useRevokedAccounts";

type AppLayoutProps = {
  children: React.ReactNode;
};

export function AppLayout({ children }: AppLayoutProps) {
  const accountId = useAuthStore((s) => s.accountId);
  const availableAccounts = useAuthStore((s) => s.availableAccounts);
  const { data: currentUser, isLoading, error, refetch } = useCurrentUserProfile();
  const revokedAccounts = useRevokedAccounts(currentUser);

  // An account the person cannot enter may be one their access to has ended, and the cached profile (kept
  // for hours, and across a session that merely expired) can predate that: it would still call the
  // employment active, and the person would be shown first-run onboarding. So on the account screen the
  // profile is read again before anything is decided from it.
  const mayHaveLostAccess = accountId === null && availableAccounts.some((a) => a.accessible === false);
  const [profileConfirmed, setProfileConfirmed] = useState(false);
  useEffect(() => {
    if (!mayHaveLostAccess) {
      setProfileConfirmed(false);
      return;
    }
    let cancelled = false;
    void refetch().finally(() => {
      if (!cancelled) setProfileConfirmed(true);
    });
    return () => {
      cancelled = true;
    };
  }, [mayHaveLostAccess, refetch]);

  const matches = useMatches();
  const routeFlag = matches.at(-1)?.staticData as
    | { authorize: boolean; requiresAccount?: boolean; noShell?: boolean }
    | undefined;
  const requiresAccount = routeFlag?.requiresAccount !== false;

  if (routeFlag?.noShell) {
    return <>{children}</>;
  }

  if (isLoading || (mayHaveLostAccess && !profileConfirmed)) {
    return <LoadingPage />;
  }

  if (error || !currentUser) {
    return (
      <ErrorPage
        title="Failed to Load Application"
        message="Unable to load your profile. Please try logging in again."
      />
    );
  }

  if (accountId !== null) {
    return <OrgShell currentUser={currentUser}>{children}</OrgShell>;
  }

  if (!requiresAccount) {
    return <UserShell currentUser={currentUser}>{children}</UserShell>;
  }

  // Someone whose access to an organisation has ended is not new: `onboardingCompleted` is false for
  // them only because they never got to finish it, and the welcome flow is the wrong thing to show a
  // person who has just lost their place. They get the account screen, which says what happened.
  if (currentUser.onboardingCompleted || revokedAccounts.length > 0) {
    return (
      <UserShell currentUser={currentUser}>
        <AccountlessHome revokedAccounts={revokedAccounts} />
      </UserShell>
    );
  }

  return <OnboardingExperience currentUser={currentUser} />;
}
